import React, { useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dropdown,
  IconButton,
  ListDivider,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalDialog,
} from '@mui/joy';
import { FaEllipsisV } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { IconWrapper } from '../../../components/IconWrapper';
import { toFreeAgentBillAppUrl } from '../../../freeagent/appUrl';
import { allowedBillActions, type AccountBill } from '../bills.model';
import { billsService } from '../bills.service';

/** Bill page route. */
export function billPath(id: string): string {
  return `/bills/${id}`;
}

/** Row and header menu for a bill. Edit, send and mark paid open dialogs owned by the page. */
export function BillActionsMenu({
  bill,
  onEdit,
  onSend,
  onMarkPaid,
  onChanged,
  onDeleted,
  hideOpen,
}: {
  bill: AccountBill;
  onEdit: (bill: AccountBill) => void;
  onSend: (bill: AccountBill) => void;
  onMarkPaid: (bill: AccountBill) => void;
  onChanged: (bill: AccountBill) => void;
  onDeleted: (bill: AccountBill) => void;
  hideOpen?: boolean;
}) {
  const navigate = useNavigate();
  const allowed = allowedBillActions(bill);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : String((err as any)?.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{ root: { variant: 'plain', color: 'neutral', size: 'sm', disabled: busy, 'aria-label': `Actions for ${bill.reference}` } as any }}
          onClick={(event) => event.stopPropagation()}
        >
          <IconWrapper icon={FaEllipsisV} />
        </MenuButton>
        <Menu placement="bottom-end" size="sm" onClick={(event) => event.stopPropagation()}>
          {!hideOpen && <MenuItem onClick={() => navigate(billPath(bill.id))}>Open</MenuItem>}
          <MenuItem onClick={() => run(() => billsService.downloadPdf(bill))}>Download PDF</MenuItem>
          {bill.attachmentPath && (
            <MenuItem
              onClick={() => run(async () => {
                const url = await billsService.attachmentUrl(bill);
                if (url) window.open(url, '_blank', 'noopener');
              })}
            >
              Their invoice
            </MenuItem>
          )}
          {allowed.edit && <MenuItem onClick={() => onEdit(bill)}>Edit…</MenuItem>}
          {bill.freeagentBillUrl && (
            <MenuItem onClick={() => window.open(toFreeAgentBillAppUrl(bill.freeagentBillUrl), '_blank', 'noopener')}>
              Open in FreeAgent
            </MenuItem>
          )}
          {allowed.send && <MenuItem onClick={() => onSend(bill)}>Send to FreeAgent…</MenuItem>}
          {allowed.updateFreeAgent && <MenuItem onClick={() => onSend(bill)}>Update FreeAgent…</MenuItem>}
          {allowed.refresh && (
            <MenuItem onClick={() => run(async () => onChanged(await billsService.refreshFromFreeAgent(bill.id)))}>
              Refresh from FreeAgent
            </MenuItem>
          )}
          {(allowed.markPaid || allowed.unmarkPaid) && <ListDivider />}
          {allowed.markPaid && <MenuItem onClick={() => onMarkPaid(bill)}>Mark as paid…</MenuItem>}
          {allowed.unmarkPaid && (
            <MenuItem onClick={() => run(async () => onChanged(await billsService.unmarkPaid(bill)))}>Mark as unpaid</MenuItem>
          )}
          {allowed.delete && (
            <>
              <ListDivider />
              <MenuItem
                color="danger"
                onClick={() => {
                  const where = bill.freeagentBillUrl ? ' It is deleted from FreeAgent too.' : '';
                  if (!window.confirm(`Delete bill ${bill.reference}?${where}`)) return;
                  void run(async () => {
                    await billsService.remove(bill);
                    onDeleted(bill);
                  });
                }}
              >
                Delete
              </MenuItem>
            </>
          )}
        </Menu>
      </Dropdown>

      <Modal open={!!error} onClose={() => setError(null)}>
        <ModalDialog size="sm">
          <DialogTitle>{bill.reference}</DialogTitle>
          <DialogContent><Alert color="danger">{error}</Alert></DialogContent>
          <DialogActions><Button onClick={() => setError(null)}>OK</Button></DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
}
